import type { ExcelExportParams, GridApi } from "ag-grid-community";
import { MILL_1, MILL_2 } from "@/setup/mill-config/mill-config";
import type { SectionsExportData } from "../../models/SectionExport";
import {
	buildSectionsSheetColumns,
	buildSectionsSheetRows,
	fitSectionsSheetColumnWidth,
	type SectionsSheetRow,
} from "./sectionsSheet";
import {
	buildSpecificationsSheetColumns,
	buildSpecificationsSheetRows,
	type SpecificationsSheetRow,
} from "./specificationsSheet";
import {
	buildStandSetupsSheetColumns,
	buildStandSetupsSheetRows,
	type StandSetupsSheetRow,
} from "./standSetupsSheet";

export interface SectionExportGridApis {
	sections: GridApi<SectionsSheetRow>;
	standSetups: GridApi<StandSetupsSheetRow>;
	specifications: GridApi<SpecificationsSheetRow>;
}

const SHEET_PARAMS: ExcelExportParams = {
	headerRowHeight: 32,
	rowHeight: 20,
	freezeRows: "headers",
	freezeColumns: "pinned",
};

const MILL_SHEETS = [
	{ millId: MILL_1.id, prefix: "M1" },
	{ millId: MILL_2.id, prefix: "M2" },
];

export const exportSectionsWorkbook = ({
	apis,
	data,
	fileName,
}: {
	apis: SectionExportGridApis;
	data: SectionsExportData;
	fileName: string;
}) => {
	const sheets: (string | undefined)[] = [];

	for (const { millId, prefix } of MILL_SHEETS) {
		const sectionRows = buildSectionsSheetRows(data, millId);
		const sectionIds = sectionRows.map((row) => row.Section.SectionId);
		const specificationRows = buildSpecificationsSheetRows(
			data,
			sectionRows.map((row) => row.Section),
		);

		apis.sections.setGridOption(
			"columnDefs",
			buildSectionsSheetColumns(millId),
		);
		apis.sections.setGridOption("rowData", sectionRows);
		sheets.push(
			apis.sections.getSheetDataForExcel({
				...SHEET_PARAMS,
				sheetName: `${prefix} Sections`,
				columnWidth: fitSectionsSheetColumnWidth(apis.sections),
			}),
		);

		apis.specifications.setGridOption(
			"columnDefs",
			buildSpecificationsSheetColumns(
				data.items.filter((item) => item.AssetId === millId),
				specificationRows.flatMap((row) => (row.Spec ? [row.Spec] : [])),
			),
		);
		apis.specifications.setGridOption("rowData", specificationRows);
		sheets.push(
			apis.specifications.getSheetDataForExcel({
				...SHEET_PARAMS,
				sheetName: `${prefix} Section Specifications`,
			}),
		);

		if (millId === MILL_2.id) {
			apis.standSetups.setGridOption(
				"columnDefs",
				buildStandSetupsSheetColumns(data.stands),
			);
			apis.standSetups.setGridOption(
				"rowData",
				buildStandSetupsSheetRows(data, sectionIds),
			);
			sheets.push(
				apis.standSetups.getSheetDataForExcel({
					...SHEET_PARAMS,
					sheetName: `${prefix} Stand Setups`,
				}),
			);
		}
	}

	apis.sections.exportMultipleSheetsAsExcel({
		data: sheets.filter((sheet): sheet is string => sheet !== undefined),
		fileName,
	});
};
